import React, { useState, useEffect } from 'react';
import {
    ShieldCheck,
    CheckCircle2,
    XCircle,
    FileText,
    Calendar,
    User,
    Users,
    Hash,
    AlertTriangle,
    Download,
    Eye,
    Clock,
    Monitor,
    Globe,
    Lock,
    Award,
    Activity,
    BarChart3,
    History,
    FileBarChart,
    RefreshCw,
    FileSignature
} from 'lucide-react';

import { apiService } from '../services/api';
import { documentService } from '../services/documentService';

const API_URL = apiService.baseURL;

export default function VerificationView({ token }) {
    const [verificationData, setVerificationData] = useState(null);
    const [verificationHistory, setVerificationHistory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (token) {
            verifyDocument(token);
            loadVerificationHistory(token);
        }
    }, [token]);

    const verifyDocument = async (docToken) => {
        setLoading(true);
        setError('');

        try {
            const data = await documentService.verifyDocument(docToken);
            if (data.success) {
                setVerificationData(data);
                loadVerificationHistory(docToken);
            } else {
                setError(data.error || 'No se pudo verificar el documento');
            }
        } catch (err) {
            setError(err.message || 'Error al conectar con el servidor');
        } finally {
            setLoading(false);
        }
    };

    const loadVerificationHistory = async (docToken) => {
        try {
            const data = await documentService.getVerificationHistory(docToken);
            if (data.success) {
                setVerificationHistory(data);
            }
        } catch (error) {
            // console.error('Error cargando historial:', error);
        }
    };

    const downloadSignedDocument = async () => {
        try {
            const response = await fetch(`${API_URL}/documents/${token}/download/signed`);
            if (!response.ok) throw new Error('Error al descargar el documento');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `firmado-${verificationData.document.originalName}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            alert('Error al descargar el documento firmado');
        }
    };

    const downloadCompleteReport = async () => {
        try {
            const report = `
INFORME COMPLETO DE VERIFICACIÓN - SISTEMA DE FIRMA DIGITAL
===========================================================

INFORMACIÓN DEL DOCUMENTO:
--------------------------
Documento: ${verificationData.document.originalName}
Firmante Principal: ${verificationData.document.clientName}
Fecha de Última Firma: ${formatDate(verificationData.document.signedAt)}
ID de Certificado: ${verificationData.document.certificateId}

RESULTADO ACTUAL DE VERIFICACIÓN: ${verificationData.valid ? 'VÁLIDO ✓' : 'INVÁLIDO ⚠️'}

FIRMANTES DEL DOCUMENTO:
------------------------
${verificationData.signers?.map((s, i) => 
  `${i + 1}. ${s.fullName} - ${s.status === 'signed' ? 'Firmado el ' + formatShortDate(s.signedAt) : 'Pendiente'} (IP: ${s.ipAddress || 'N/A'})`
).join('\n') || 'No hay firmantes registrados'}

HASHES DIGITALES:
-----------------
Hash Original: ${verificationData.document.originalHash}
Hash Firmado (Almacenado): ${verificationData.document.signedHash}
Hash Actual (Verificación): ${verificationData.document.currentHash}

ESTADÍSTICAS DE VERIFICACIÓN:
-----------------------------
Total de Verificaciones: ${verificationData.verificationStats?.totalVerifications || 0}
Verificaciones Válidas: ${verificationData.verificationStats?.validVerifications || 0}
Verificaciones Inválidas: ${verificationData.verificationStats?.invalidVerifications || 0}
Última Verificación: ${formatDate(verificationData.verificationStats?.lastVerification)}

INFORMACIÓN DE SEGURIDAD:
-------------------------
IP de Firma Original: ${verificationData.document.ipAddress || 'N/A'}
Agente de Usuario: ${verificationData.document.userAgent || 'N/A'}

MENSAJE DEL SISTEMA: ${verificationData.message}

HISTORIAL DE VERIFICACIONES:
---------------------------
${verificationHistory?.verificationHistory?.map((log, index) => `
Verificación #${index + 1}:
- Fecha: ${formatDate(log.verifiedAt)}
- Resultado: ${log.isValid ? 'VÁLIDO' : 'INVÁLIDO'}
- IP: ${log.ipAddress || 'N/A'}
- Hash: ${log.currentHash ? truncateHash(log.currentHash, 32) : 'N/A'}
`).join('\n') || 'No hay historial de verificaciones'}

FECHA DE GENERACIÓN DEL REPORTE: ${new Date().toLocaleString('es-ES')}
URL DE VERIFICACIÓN: ${window.location.href}
================================================================
      `.trim();

            const blob = new Blob([report], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `reporte-verificacion-${verificationData.document.certificateId}.txt`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            alert('Error al descargar el reporte completo');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('es-ES', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('es-ES', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const truncateHash = (hash, length = 16) => {
        if (!hash) return 'N/A';
        return `${hash.substring(0, length)}...${hash.substring(hash.length - 8)}`;
    };

    // ================= ESTADO DE CARGA =================
    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="relative mb-6 mx-auto w-20 h-20">
                        <div className="absolute inset-0 border-4 border-slate-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                        <ShieldCheck className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">Verificando integridad criptográfica</h3>
                    <p className="text-sm text-slate-500">Validando hashes y certificados digitales...</p>
                </div>
            </div>
        );
    }

    // ================= ESTADO DE ERROR =================
    if (error) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 max-w-lg w-full text-center">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
                        <XCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-2">Error de Verificación</h2>
                    <p className="text-slate-600 mb-6">{error}</p>
                    
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-left mb-6">
                        <p className="text-sm font-medium text-slate-700 mb-2">Causas comunes:</p>
                        <ul className="space-y-2 text-sm text-slate-600">
                            <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5">•</span> El token de verificación ha expirado o es inválido.</li>
                            <li className="flex items-start gap-2"><span className="text-red-500 mt-0.5">•</span> El documento no fue procesado por este sistema.</li>
                        </ul>
                    </div>
                    
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full bg-slate-900 text-white px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm flex items-center justify-center gap-2"
                    >
                        <RefreshCw className="w-4 h-4" /> Reintentar
                    </button>
                </div>
            </div>
        );
    }

    const isValid = verificationData?.valid;
    const doc = verificationData?.document;
    const stats = verificationData?.verificationStats;

    // ================= ESTADO EXITOSO =================
    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Barra Superior de Confianza */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-900 rounded-md flex items-center justify-center">
                            <FileSignature className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-semibold text-slate-900 tracking-tight">Verificación</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs font-medium text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
                        <Lock className="w-3 h-3" />
                        <span>Conexión Segura (TLS 1.3)</span>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Banner de Estado Principal */}
                <div className={`rounded-xl border p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 ${
                    isValid 
                        ? 'bg-emerald-50/50 border-emerald-200' 
                        : 'bg-red-50/50 border-red-200'
                }`}>
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isValid ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                        {isValid ? (
                            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                        ) : (
                            <AlertTriangle className="w-7 h-7 text-red-600" />
                        )}
                    </div>
                    <div className="flex-1">
                        <h1 className={`text-2xl font-bold tracking-tight mb-1 ${
                            isValid ? 'text-emerald-900' : 'text-red-900'
                        }`}>
                            {isValid ? 'Documento Auténtico y Válido' : '⚠ Documento Alterado o Inválido'}
                        </h1>
                        <p className={`text-sm leading-relaxed ${
                            isValid ? 'text-emerald-800' : 'text-red-800'
                        }`}>
                            {verificationData?.message || 'Estado de verificación criptográfica'}
                        </p>
                        {isValid && (
                            <div className="flex flex-wrap gap-2 mt-3">
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-md text-xs font-medium text-emerald-700 border border-emerald-200 shadow-sm">
                                    <Award className="w-3 h-3" /> Firma Certificada
                                </span>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white rounded-md text-xs font-medium text-emerald-700 border border-emerald-200 shadow-sm">
                                    <ShieldCheck className="w-3 h-3" /> Integridad Verificada
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Columna Principal: Datos */}
                    <div className="lg:col-span-2 space-y-6">
                        
                        {/* 1. Información del Documento */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <h3 className="text-base font-semibold text-slate-900 mb-5 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-slate-500" />
                                Información del Documento
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="md:col-span-2">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Nombre del Archivo</p>
                                    <p className="text-sm font-semibold text-slate-900 bg-slate-50 p-3 rounded-lg border border-slate-100 break-all">
                                        {doc?.originalName}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                        <User className="w-3 h-3" /> Firmante Principal
                                    </p>
                                    <p className="text-sm font-medium text-slate-900">{doc?.clientName}</p>
                                </div>
                                <div>
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                        <Calendar className="w-3 h-3" /> Fecha de Última Firma
                                    </p>
                                    <p className="text-sm font-medium text-slate-900">{formatDate(doc?.signedAt)}</p>
                                </div>
                                <div className="md:col-span-2">
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                        <Award className="w-3 h-3" /> ID de Certificado
                                    </p>
                                    <p className="text-sm font-mono font-medium text-blue-700 bg-blue-50/50 p-2 rounded border border-blue-100 inline-block">
                                        {doc?.certificateId}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* 2. Firmantes del Documento */}
                        {verificationData?.signers && verificationData.signers.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                <h3 className="text-base font-semibold text-slate-900 mb-5 flex items-center gap-2">
                                    <Users className="w-5 h-5 text-slate-500" />
                                    Firmantes ({verificationData.signers.length})
                                </h3>
                                <div className="space-y-3">
                                    {verificationData.signers.map((signer, index) => (
                                        <div key={signer.id} className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                                                {index + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-slate-900 truncate">{signer.fullName}</p>
                                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                                                    {signer.signedAt ? (
                                                        <span className="flex items-center gap-1 text-emerald-600">
                                                            <CheckCircle2 className="w-3 h-3" />
                                                            Firmado el {formatShortDate(signer.signedAt)}
                                                        </span>
                                                    ) : (
                                                        <span className="flex items-center gap-1 text-amber-600">
                                                            <Clock className="w-3 h-3" />
                                                            Pendiente
                                                        </span>
                                                    )}
                                                    {signer.ipAddress && (
                                                        <span className="flex items-center gap-1">
                                                            <Globe className="w-3 h-3" />
                                                            {signer.ipAddress}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 3. Hashes Criptográficos (Estilo Terminal/Profesional) */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                            <h3 className="text-base font-semibold text-slate-900 mb-5 flex items-center gap-2">
                                <Hash className="w-5 h-5 text-slate-500" />
                                Huellas Digitales SHA-256
                            </h3>
                            <div className="space-y-5">
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Hash del Documento Original</p>
                                    <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
                                        <p className="text-xs font-mono text-slate-300 break-all leading-relaxed">{doc?.originalHash}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Hash Firmado (Almacenado en Sistema)</p>
                                    <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
                                        <p className="text-xs font-mono text-slate-300 break-all leading-relaxed">{doc?.signedHash}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Hash Actual (Verificación en Tiempo Real)</p>
                                    <div className={`rounded-lg p-3 border ${isValid ? 'bg-emerald-900 border-emerald-800' : 'bg-red-900 border-red-800'}`}>
                                        <p className="text-xs font-mono text-white break-all leading-relaxed">{doc?.currentHash}</p>
                                    </div>
                                </div>

                                {/* Veredicto de Hashes */}
                                <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                                    isValid ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                                }`}>
                                    {isValid ? (
                                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    )}
                                    <div>
                                        <p className={`text-sm font-semibold ${isValid ? 'text-emerald-900' : 'text-red-900'}`}>
                                            {isValid ? 'Integridad Criptográfica Confirmada' : 'Discrepancia de Hashes Detectada'}
                                        </p>
                                        <p className={`text-xs mt-1 ${isValid ? 'text-emerald-700' : 'text-red-700'}`}>
                                            {isValid 
                                                ? 'Los hashes coinciden perfectamente. El documento no ha sufrido alteraciones desde su firma.' 
                                                : 'El contenido del documento ha sido modificado después de la firma digital original. La validez legal queda anulada.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 4. Historial de Verificaciones */}
                        {verificationHistory?.verificationHistory && verificationHistory.verificationHistory.length > 0 && (
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                <h3 className="text-base font-semibold text-slate-900 mb-5 flex items-center gap-2">
                                    <History className="w-5 h-5 text-slate-500" />
                                    Historial de Auditoría
                                </h3>
                                <div className="space-y-3 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                                    {verificationHistory.verificationHistory.map((log, index) => (
                                        <div key={log.id} className="flex items-start gap-4 p-3 rounded-lg border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                                            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${log.isValid ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-xs font-semibold ${log.isValid ? 'text-emerald-700' : 'text-red-700'}`}>
                                                        {log.isValid ? 'Verificación Exitosa' : 'Verificación Fallida'}
                                                    </span>
                                                    <span className="text-xs text-slate-500 font-mono">{formatShortDate(log.verifiedAt)}</span>
                                                </div>
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 font-mono">
                                                    <span>IP: {log.ipAddress || 'N/A'}</span>
                                                    <span className="truncate max-w-[200px]">Hash: {truncateHash(log.currentHash, 24)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Columna Lateral: Acciones y Estadísticas */}
                    <div className="space-y-6">
                        
                        {/* Estadísticas Rápidas */}
                        {stats && (
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4" /> Métricas de Verificación
                                </h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 text-center">
                                        <p className="text-xl font-bold text-slate-900">{stats.totalVerifications || 0}</p>
                                        <p className="text-[10px] font-medium text-slate-500 uppercase mt-1">Total</p>
                                    </div>
                                    <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100 text-center">
                                        <p className="text-xl font-bold text-emerald-700">{stats.validVerifications || 0}</p>
                                        <p className="text-[10px] font-medium text-emerald-600 uppercase mt-1">Válidas</p>
                                    </div>
                                    <div className="p-3 bg-red-50 rounded-lg border border-red-100 text-center col-span-2">
                                        <p className="text-xl font-bold text-red-700">{stats.invalidVerifications || 0}</p>
                                        <p className="text-[10px] font-medium text-red-600 uppercase mt-1">Inválidas / Alteradas</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Panel de Acciones (Sticky) */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 sticky top-24">
                            <h3 className="text-sm font-semibold text-slate-900 mb-4">Acciones Disponibles</h3>
                            <div className="space-y-3">
                                <button
                                    onClick={downloadSignedDocument}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 active:bg-slate-950 transition-all font-medium text-sm shadow-sm"
                                >
                                    <Download className="w-4 h-4" />
                                    Descargar PDF Firmado
                                </button>

                                <button
                                    onClick={downloadCompleteReport}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all font-medium text-sm"
                                >
                                    <FileBarChart className="w-4 h-4" />
                                    Exportar Reporte (.txt)
                                </button>

                                <div className="grid grid-cols-2 gap-3 pt-2">
                                    <button
                                        onClick={() => window.print()}
                                        className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-all font-medium text-xs"
                                    >
                                        <Eye className="w-3.5 h-3.5" /> Imprimir
                                    </button>
                                    <button
                                        onClick={() => verifyDocument(token)}
                                        className="flex items-center justify-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-all font-medium text-xs"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5" /> Actualizar
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Badge de Seguridad */}
                        <div className="bg-slate-900 rounded-xl p-5 text-white shadow-lg">
                            <div className="flex items-center gap-2 mb-3">
                                <ShieldCheck className="w-5 h-5 text-blue-400" />
                                <h3 className="font-semibold text-sm">Estándares de Seguridad</h3>
                            </div>
                            <ul className="space-y-2.5 text-xs text-slate-300">
                                <li className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    Algoritmo de Hashing SHA-256
                                </li>
                                <li className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    Sello de tiempo certificado (RFC 3161)
                                </li>
                                <li className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    Trazabilidad de auditoría inmutable
                                </li>
                                <li className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                    Cumplimiento normativo eIDAS
                                </li>
                            </ul>
                        </div>

                    </div>
                </div>
            </main>
            
            {/* Footer simple */}
            <footer className="border-t border-slate-200 bg-white mt-12 py-6">
                <div className="max-w-7xl mx-auto px-4 text-center text-xs text-slate-500">
                    <p>© 2026 Inkless. Todos los derechos reservados.</p>
                    <p className="mt-1">Este informe de verificación tiene validez como comprobante de integridad documental.</p>
                </div>
            </footer>
        </div>
    );
}